import type { ComboCoverageViewModel } from 'src/api/types/ComboCoverageViewModel';

import { useNavigate } from 'react-router';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {
  Card,
  Stack,
  Checkbox,
  Typography,
  CardContent,
  FormControlLabel,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { EmptyContent } from 'src/components/empty-content';

import { getAddressToString } from 'src/sections/employer-data/shared/utils/policyUtils';
import { PolicyStatusChips } from 'src/sections/employer-data/shared/components/status-chips';
import { MoveCoverageModal } from 'src/sections/employer-data/coverages/components/move-coverage-modal';

import { InfoBox } from '../../shared/components/info-box';
import { ActionButton } from '../../shared/components/action-button';

// ----------------------------------------------------------------------

type ComboProps = {
  comboSummary: ComboCoverageViewModel;
};

export function ComboGeneral({ comboSummary }: ComboProps) {
  const navigate = useNavigate();
  const comboId = comboSummary.comboId;
  const coverageList = useMemo(() => comboSummary.coverages ?? [], [comboSummary.coverages]);
  const hasCoverages = coverageList && coverageList.length > 0;
  const [selectedCoverageIds, setSelectedCoverageIds] = useState<Set<number>>(new Set());
  const [activeCoverageIdForMove, setActiveCoverageIdForMove] = useState<number | undefined>(
    undefined
  );
  const [activeCoverageIdsForBatchMove, setActiveCoverageIdsForBatchMove] = useState<number[]>([]);
  const [moveCoveragesOpen, setMoveCoveragesOpen] = useState(false);

  const selectableCoverageIdSet = useMemo(
    () =>
      new Set(
        (coverageList ?? [])
          .map((coverage) => coverage.coverageId)
          .filter((id): id is number => id != null)
      ),
    [coverageList]
  );

  useEffect(() => {
    setSelectedCoverageIds((previous) => {
      const next = new Set<number>();

      previous.forEach((id) => {
        if (selectableCoverageIdSet.has(id)) {
          next.add(id);
        }
      });

      return next;
    });
  }, [selectableCoverageIdSet]);

  useEffect(() => {
    setSelectedCoverageIds(new Set());
  }, [comboId]);

  const toggleCoverageSelection = useCallback((coverageId?: number) => {
    if (coverageId == null) {
      return;
    }

    setSelectedCoverageIds((previous) => {
      const next = new Set(previous);
      if (next.has(coverageId)) {
        next.delete(coverageId);
      } else {
        next.add(coverageId);
      }

      return next;
    });
  }, []);

  const handleBulkMoveOpen = useCallback(() => {
    const selectedIdsInListOrder = (coverageList ?? [])
      .map((coverage) => coverage.coverageId)
      .filter((id): id is number => id != null && selectedCoverageIds.has(id));

    setActiveCoverageIdForMove(undefined);
    setActiveCoverageIdsForBatchMove(selectedIdsInListOrder);
    setMoveCoveragesOpen(true);
  }, [coverageList, selectedCoverageIds]);

  const handleSingleMoveOpen = useCallback((coverageId?: number) => {
    if (coverageId == null) {
      return;
    }

    setActiveCoverageIdForMove(coverageId);
    setActiveCoverageIdsForBatchMove([]);
    setMoveCoveragesOpen(true);
  }, []);

  const handleBulkMoveClose = useCallback(() => {
    setMoveCoveragesOpen(false);
    setActiveCoverageIdForMove(undefined);
    setActiveCoverageIdsForBatchMove([]);
  }, []);

  const handleBatchMoveSuccess = useCallback(() => {
    setSelectedCoverageIds(new Set());
  }, []);

  const modalCoverageIds =
    activeCoverageIdsForBatchMove.length > 0 ? activeCoverageIdsForBatchMove : undefined;

  const renderContent = () => (
    <>
      {hasCoverages && (
        <Card
          data-testid="combo-profile-card"
          sx={{
            display: 'flex',
            zIndex: 'unset',
            overflow: 'unset',
            mt: 5,
            borderRadius: 2,
            boxShadow: 'var(--customShadows-card)',
            width: '75%',
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              sx={{ color: 'text.primary' }}
              data-testid="combo-profile-title"
            >
              Combo profile
            </Typography>
            <Grid container spacing={2} columns={16} sx={{ mt: 3 }}>
              <Grid size={{ xs: 16, md: 8 }}>
                <InfoBox
                  label="Combo name"
                  value={comboSummary.controllingCoverage?.comboName ?? ''}
                  testId="combo-name-field"
                  testIdLabel="combo-name-label"
                  testIdValue="combo-name-value"
                  sx={{ mt: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 16, md: 8 }}>
                <InfoBox
                  label="Combo ID"
                  value={comboSummary.controllingCoverage?.comboId ?? ''}
                  testId="combo-id-field"
                  testIdLabel="combo-id-label"
                  testIdValue="combo-id-value"
                  sx={{ mt: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 16 }}>
                <InfoBox
                  label="Interstate ID"
                  value={comboSummary.controllingCoverage?.interstateId ?? ''}
                  testId="interstate-id-field"
                  testIdLabel="interstate-id-label"
                  testIdValue="interstate-id-value"
                  sx={{ mt: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 16 }}>
                <InfoBox
                  label="Address"
                  value={getAddressToString(comboSummary.controllingCoverage?.primaryAddress)}
                  testId="address-field"
                  testIdLabel="address-label"
                  testIdValue="address-value"
                  sx={{ mt: 0, whiteSpace: 'pre-line' }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card
        sx={{
          display: 'flex',
          zIndex: 'unset',
          overflow: 'unset',
          mt: 3,
          borderRadius: 2,
          boxShadow: 'var(--customShadows-card)',
          width: '75%',
        }}
      >
        <CardContent sx={{ flexGrow: 1, pr: 1.5 }} data-testid="coverages-card-content">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, p: 2 }}>
            <Typography variant="h6" sx={{ color: 'text.primary' }} data-testid="coverages-title">
              Coverages ({coverageList?.length ?? 0})
            </Typography>
            {selectedCoverageIds.size >= 2 && (
              <ActionButton
                label="Move coverages"
                testId="move-coverages-button"
                styleVariant="light-outlined"
                icon="lucide:folder-input"
                size="small"
                onClick={handleBulkMoveOpen}
              />
            )}
          </Box>

          {!hasCoverages ? (
            <EmptyContent
              title="There are no coverages in this combo."
              description="Review the timeline to see historical information."
              sx={{ pb: 10 }}
              data-testid="combo-coverages-empty-content"
            />
          ) : (
            <Box sx={{ maxHeight: '57rem', overflowY: 'auto', p: 2 }}>
              <Stack spacing={2} data-testid="coverages-list">
                {coverageList?.map((coverage, index) => (
                  <Box
                    component={RouterLink as React.ElementType}
                    to={`/dashboard/employer-data/combo/${comboId}/coverage/${coverage?.coverageId}`}
                    key={coverage.coverageId || `coverage-${index}`}
                    sx={{
                      p: 1,
                      paddingTop: 2,
                      paddingBottom: 3,
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: 'shared.cardOutlined',
                      position: 'relative',
                      textDecoration: 'none',
                      display: 'block',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    data-testid={`coverage-item-${index}`}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            sx={{ ml: 1.5, mr: 1.5 }}
                            checked={
                              coverage.coverageId != null &&
                              selectedCoverageIds.has(coverage.coverageId)
                            }
                            disabled={coverage.coverageId == null}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={() => toggleCoverageSelection(coverage.coverageId)}
                            slotProps={{
                              input: {
                                'aria-label': `Select coverage ${coverage?.primaryName || index + 1}`,
                              },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography
                              sx={{
                                typography: 'subtitle1',
                                color: 'text.primary',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                              data-testid={`coverage-item-name-${index}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (coverage?.coverageId != null) {
                                  navigate(
                                    `/dashboard/employer-data/combo/${comboId}/coverage/${coverage.coverageId}`
                                  );
                                }
                              }}
                            >
                              {coverage?.primaryName || ''}
                            </Typography>
                            <PolicyStatusChips
                              isControllingCoverage={coverage?.isControllingCoverage}
                            />
                          </Box>
                        }
                        sx={{ mr: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <ActionButton
                          label="Move coverage"
                          testId={`move-coverage-row-button-${index}`}
                          styleVariant="light-outlined"
                          icon="lucide:folder-input"
                          size="small"
                          onClick={() => handleSingleMoveOpen(coverage.coverageId)}
                        />
                      </Box>
                    </Box>
                    <Typography
                      sx={{ typography: 'body2', color: 'text.secondary', ml: 6 }}
                      data-testid={`coverage-item-address-${index}`}
                    >
                      {getAddressToString(coverage?.primaryAddress)}
                    </Typography>

                    <Grid container spacing={2} columns={4} sx={{ ml: 6 }}>
                      <Grid size={2}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 0.2 }}
                          data-testid={`coverage-item-id-label-${index}`}
                        >
                          <Typography
                            component="span"
                            sx={{ typography: 'body2', color: 'text.secondary' }}
                          >
                            Coverage ID
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            component="span"
                            sx={{ typography: 'subtitle2', color: 'text.primary' }}
                            data-testid={`coverage-item-id-value-${index}`}
                          >
                            {coverage?.coverageId ?? ''}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={2}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 0.2 }}
                          data-testid={`coverage-item-fein-label-${index}`}
                        >
                          <Typography
                            component="span"
                            sx={{ typography: 'body2', color: 'text.secondary' }}
                          >
                            FEIN
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            component="span"
                            sx={{ typography: 'subtitle2', color: 'text.primary' }}
                            data-testid={`coverage-item-fein-value-${index}`}
                          >
                            {coverage?.feinLastFive || ''}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );

  return (
    <>
      {renderContent()}
      <MoveCoverageModal
        open={moveCoveragesOpen}
        onClose={handleBulkMoveClose}
        comboId={comboSummary.comboId ?? 0}
        coverageId={activeCoverageIdForMove}
        coverageIds={modalCoverageIds}
        onBatchMoveSuccess={handleBatchMoveSuccess}
        originPage="combo"
      />
    </>
  );
}
